import React, { useState, useEffect } from 'react';
import { FaEnvelope, FaSave, FaBell, FaClock, FaBullhorn } from 'react-icons/fa';
import api from '../../utils/api/axiosInstance';
import styles from '../../styles/components/profile/EmailPreferencesSection.module.css';
import profileStyles from '../../styles/pages/profile/ProfilePage.module.css';
import CollapsibleSection from '../common/CollapsibleSection';

const EmailPreferencesSection = ({ showToast }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [preferences, setPreferences] = useState({
    // Notification emails
    newFollowers: true,
    messages: true,
    postLikes: true,
    postComments: true,
    questionAnswers: true,
    reviewReplies: true,
    
    // Digest emails
    weeklyDigest: false,
    monthlyDigest: false,
    
    // Marketing
    marketing: false,
    productRecommendations: false,
  });

  useEffect(() => {
    fetchPreferences();
  }, []);

  const fetchPreferences = async () => {
    try {
      setLoading(true);
      // This endpoint will auto-create preferences if they don't exist
      const response = await api.get('/users/me/email-preferences');
      
      if (response.data.success) {
        setPreferences(response.data.preferences);
      }
    } catch (error) {
      console.error('Error fetching email preferences:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = (key) => {
    setPreferences((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const response = await api.put('/users/me/email-preferences', preferences);
      
      if (response.data.success) {
        showToast?.({ message: 'Email preferences updated', type: 'success' });
      }
    } catch (error) {
      console.error('Error saving email preferences:', error);
      showToast?.({ message: 'Failed to update preferences', type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>Loading email preferences...</div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h3>
          <FaEnvelope /> Email Preferences
        </h3>
        <p>Control which emails you receive from Simpary</p>
      </div>

      <CollapsibleSection
        title="Notifications"
        subtitle="Receive email notifications for activity on your account"
        titleIcon={<FaBell className={profileStyles.sectionIcon} />}
        defaultExpanded={false}
      >
        
        <div className={styles.preferencesList}>
          <label className={styles.preferenceItem}>
            <div className={styles.preferenceInfo}>
              <span className={styles.preferenceName}>New Followers</span>
              <span className={styles.preferenceDesc}>
                Get notified when someone follows you
              </span>
            </div>
            <input
              type="checkbox"
              checked={preferences.newFollowers}
              onChange={() => handleToggle('newFollowers')}
              className={styles.toggle}
            />
          </label>

          <label className={styles.preferenceItem}>
            <div className={styles.preferenceInfo}>
              <span className={styles.preferenceName}>Messages</span>
              <span className={styles.preferenceDesc}>
                Get notified when you receive a new message
              </span>
            </div>
            <input
              type="checkbox"
              checked={preferences.messages}
              onChange={() => handleToggle('messages')}
              className={styles.toggle}
            />
          </label>

          <label className={styles.preferenceItem}>
            <div className={styles.preferenceInfo}>
              <span className={styles.preferenceName}>Post Interactions</span>
              <span className={styles.preferenceDesc}>
                Get notified when someone likes or comments on your posts
              </span>
            </div>
            <input
              type="checkbox"
              checked={preferences.postLikes || preferences.postComments}
              onChange={() => {
                handleToggle('postLikes');
                handleToggle('postComments');
              }}
              className={styles.toggle}
            />
          </label>

          <label className={styles.preferenceItem}>
            <div className={styles.preferenceInfo}>
              <span className={styles.preferenceName}>Question Answers</span>
              <span className={styles.preferenceDesc}>
                Get notified when someone answers your questions
              </span>
            </div>
            <input
              type="checkbox"
              checked={preferences.questionAnswers}
              onChange={() => handleToggle('questionAnswers')}
              className={styles.toggle}
            />
          </label>

          <label className={styles.preferenceItem}>
            <div className={styles.preferenceInfo}>
              <span className={styles.preferenceName}>Review Replies</span>
              <span className={styles.preferenceDesc}>
                Get notified when someone replies to your reviews
              </span>
            </div>
            <input
              type="checkbox"
              checked={preferences.reviewReplies}
              onChange={() => handleToggle('reviewReplies')}
              className={styles.toggle}
            />
          </label>
        </div>
      </CollapsibleSection>

      <CollapsibleSection
        title="Digest Emails"
        subtitle="Receive periodic summaries of activity"
        titleIcon={<FaClock className={profileStyles.sectionIcon} />}
        defaultExpanded={false}
      >
        <div className={styles.preferencesList}>
          <label className={styles.preferenceItem}>
            <div className={styles.preferenceInfo}>
              <span className={styles.preferenceName}>Weekly Digest</span>
              <span className={styles.preferenceDesc}>
                Weekly summary of trending products and community activity
              </span>
            </div>
            <input
              type="checkbox"
              checked={preferences.weeklyDigest}
              onChange={() => handleToggle('weeklyDigest')}
              className={styles.toggle}
            />
          </label>

          <label className={styles.preferenceItem}>
            <div className={styles.preferenceInfo}>
              <span className={styles.preferenceName}>Monthly Digest</span>
              <span className={styles.preferenceDesc}>
                Monthly recap of your activity and top products
              </span>
            </div>
            <input
              type="checkbox"
              checked={preferences.monthlyDigest}
              onChange={() => handleToggle('monthlyDigest')}
              className={styles.toggle}
            />
          </label>
        </div>
      </CollapsibleSection>

      <CollapsibleSection
        title="Marketing & Recommendations"
        subtitle="Promotional emails and personalized recommendations"
        titleIcon={<FaBullhorn className={profileStyles.sectionIcon} />}
        defaultExpanded={false}
      >
        <div className={styles.preferencesList}>
          <label className={styles.preferenceItem}>
            <div className={styles.preferenceInfo}>
              <span className={styles.preferenceName}>Marketing Emails</span>
              <span className={styles.preferenceDesc}>
                Receive updates about new features and promotions
              </span>
            </div>
            <input
              type="checkbox"
              checked={preferences.marketing}
              onChange={() => handleToggle('marketing')}
              className={styles.toggle}
            />
          </label>

          <label className={styles.preferenceItem}>
            <div className={styles.preferenceInfo}>
              <span className={styles.preferenceName}>Product Recommendations</span>
              <span className={styles.preferenceDesc}>
                Get personalized product suggestions based on your interests
              </span>
            </div>
            <input
              type="checkbox"
              checked={preferences.productRecommendations}
              onChange={() => handleToggle('productRecommendations')}
              className={styles.toggle}
            />
          </label>
        </div>
      </CollapsibleSection>

      <div className={profileStyles.formActions}>
        <button
          type="button"
          className="buttonPrimary"
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? (
            <>Saving...</>
          ) : (
            <>
              <FaSave /> Save Preferences
            </>
          )}
        </button>
      </div>

      <div className={styles.notice}>
        <strong>Note:</strong> Security-related emails (password reset, account status changes) cannot be disabled for your account security.
      </div>
    </div>
  );
};

export default EmailPreferencesSection;
